import axios from 'axios';
import config from '../config';

import { store } from '../store';
import { showError } from '../utils';

// Generic network call function to handle common API request patterns
function networkCall({ method, url, headers, params, data, ...extraOptions }) {
  return axios({
    method,
    url,
    headers,
    params,
    data,
    ...extraOptions,
  })
    .then((resp) => {
      const responseBody = resp.data;
      if (responseBody.message === 'success') {
        return responseBody;
      } else {
        const error = new Error('Invalid response');
        error.data = responseBody;
        throw error;
      }
    })
    .catch((err) => {
      showError(err);
      throw err;
    });
}

export function fetchPreviousDentalAppointments(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/cashless-dental`,
    headers: {
      authorization: authToken,
    },
  });
}

export function fetchUserDentalAppointments(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/user-past-appointments`,
    headers: {
      authorization: authToken,
    },
  });
}

export function getDentalRequestStatus(appointmentId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/get-appointment-status`,
    headers: {
      authorization: authToken,
    },
    params: { appointmentId },
  });
}

export function searchDentists(latitude, longitude, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/get-clinics`,
    headers: {
      authorization: authToken,
    },
    params: {
      lat: latitude === -1 ? undefined : latitude,
      long: longitude === -1 ? undefined : longitude,
    },
  });
}

export function getDentalRelatives() {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/care/relatives?t="dental"`,
    headers: {
      authorization: store.getState().user.authToken,
    },
  });
}

export function fetchDentalSlots(authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/get-slots`,
    headers: {
      authorization: authToken,
    },
  });
}

export function getDentalInvoice(price, relativeId, authToken) {
  return networkCall({
    method: 'get',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/checkout`,
    headers: {
      authorization: authToken,
    },
    params: {
      price,
      relativeId: relativeId === 'self' ? undefined : relativeId,
    },
  });
}

export function cancelDentalSlotRequest(
  appointmentId,
  providerId,
  reason,
  authToken
) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/cancel-appointment`,
    headers: {
      authorization: authToken,
    },
    data: {
      appointmentId,
      providerId,
      reason,
    },
  });
}

export function uploadDentalPrescription(file, authToken) {
  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/users/upload`,
    headers: {
      authorization: authToken,
    },
    data: file,
  });
}

export function bookDentalAppointment(
  relativeId,
  providerId,
  clinicId,
  latitude,
  longitude,
  datekey,
  slotId,
  finalFee,
  history,
  authToken
) {
  const headers = {
    authorization: authToken,
  };
  const redirectToPaymentGateway = () => {
    let url =
      relativeId === 'self'
        ? `${
            config.apiBaseUrl
          }/cashless-dental/mdp/new-appointment?providerId=${providerId}&clinicId=${clinicId}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}&auth=${
            authToken.split(' ')[1]
          }`
        : `${
            config.apiBaseUrl
          }/cashless-dental/mdp/new-appointment?providerId=${providerId}&clinicId=${clinicId}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}&relativeId=${relativeId}&auth=${
            authToken.split(' ')[1]
          }`;

    window.location.assign(url);
    return window.location.assign(url);
  };

  if (finalFee === true || finalFee.toString() === '0') {
    return redirectToPaymentGateway();
  }

  const url =
    relativeId === 'self'
      ? `${config.apiBaseUrl}/cashless-dental/mdp/new-appointment?providerId=${providerId}&clinicId=${clinicId}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}`
      : `${config.apiBaseUrl}/cashless-dental/mdp/new-appointment?providerId=${providerId}&clinicId=${clinicId}&dateKey=${datekey}&slotId=${slotId}&latitude=${latitude}&longitude=${longitude}&relativeId=${relativeId}`;
  return window.PhonePe.PhonePe.build(window.PhonePe.Constants.Species.web)
    .then((sdk) => {
      // Call the API to fetch the data context
      return axios
        .get(url, {
          headers,
        })
        .then((res) => {
          const responseBody = res.data;
          if (responseBody.message === 'success') {
            const context = responseBody.data;
            if (sdk.isMethodSupported('openPaymentsPage')) {
              history.replace('/processing-payment', {
                orderType: 'dental',
                transactionId: responseBody.transactionId,
                successPage: `dental/preview`,
                failurePage: `dental/preview`,
                failureStateParameters: {
                  paymentFailed: true,
                },
              });
              return sdk.openPaymentsPage(
                'Visit',
                context,
                `${config.rootUrl}dental/preview`,
                'https://getvisitapp.com/img/png/Visit_logo.png',
                [
                  {
                    'Order For': 'Doctor Consultation',
                  },
                ]
              );
            }
            throw new Error('Phonepe method not supported');
          } else if (responseBody.message === 'failure') {
            history.push('/home', {
              bookingFailed: true,
              errorMessage: responseBody.errorMessage,
            });
            return;
          } else {
            throw new Error('Failed to get payment context');
          }
        });
    })
    .catch((err) => {
      try {
        const parsedError = JSON.parse(err);
        if (
          parsedError &&
          (parsedError.error_code === 'PAYMENT_DISMISS' ||
            parsedError.error_code === 'PAYMENT_TIMEOUT')
        ) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
        // in case of android, it could still be a null error.
        if (parsedError === 'null' || parsedError === null) {
          store.dispatch({
            type: 'SHOW_ERROR',
            payload: {
              errorTitle: 'Something went wrong',
              errorDesc: 'Your payment failed. Please retry your payment.',
            },
          });
          history.push('/home', {
            paymentFailed: true,
          });
          return;
        }
      } catch (e) {
        //  Since we couldn't parse the error, it means it's not a string and hence not a phonepe dismiss/timeout error.
        // Not on phonepe in-app or some other error.
        // send to payment
        return redirectToPaymentGateway();
      }
    });
}

export function procedureRequest(authToken, requestId, ids) {
  const body = {
    requestId: requestId,
  };

  // Add procedureIds only if id is not null
  if (ids.length) {
    body.procedureIds = String(ids); // Ensures the id is in string format
  }

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/procedure-requested`,
    headers: {
      Authorization: authToken,
    },
    data: body,
  });
}

export function procedureWalletDeduction(authToken, requestId, ids, history) {
  const body = {
    requestId: requestId,
  };

  if (ids.length) {
    body.procedureIds = String(ids); // Ensures the id is in string format
  }

  return networkCall({
    method: 'post',
    url: `${config.apiBaseUrl}/cashless-dental/mdp/procedures-wallet-deduction`,
    headers: {
      authorization: authToken,
    },
    data: body,
  }).then((responseBody) => {
    return history.push('/dental/appointment/appointment-status', {
      requestId: requestId,
    });
  });
}
